import {BannerTransportJs} from 'OK/banners/bannerNew/transport/BannerTransportJs';
import {BannerConstantJs} from 'OK/banners/bannerNew/utils/BannerConstantJs';
import {DispatcherJs} from 'OK/DispatcherJs';
import {BannerProxyJs} from 'OK/banners/bannerNew/banners/BannerProxyJs';
import {BannerModelJs} from 'OK/banners/bannerNew/BannerModelJs';
import {BanDataCfgJs} from 'OK/BanDataCfgJs';
import {createLog, log} from 'OK/banners/bannerNew/utils/BannerCommon';
import {BannerCfgJs} from 'OK/banners/bannerNew/config/BannerCfgJs';
import {BannerParamsJs} from 'OK/banners/bannerNew/config/BannerParamsJs';
import {BannerViewJs} from 'OK/banners/bannerNew/BannerViewJs';
import {detect as adbWhitelistDetect} from 'OK/wld';
import {
    IBannerJs,
    NavigationTriggerJs,
    SimpleBannerJs,
    TimeoutTriggerJs
} from 'OK/banners/bannerNew/banners/SimpleBannerJs';
import {ValueProviderJs} from 'OK/banners/bannerNew/ValueProviderJs';
import * as logger from 'OK/logger';
import * as utils from 'OK/utils/utils';
import {AdfBannerTransportJs} from "OK/banners/bannerNew/transport/AdfBannerTransportJs";

export interface IBannerJson {
    wu: boolean; // BannerConstantJs.WITH_UNIQUE_ID
    wv: boolean; // BannerConstantJs.WITH_PERSONAL_DATA
    la: boolean; // BannerConstantJs.WITH_LAST_APPS
    p: string; // BannerConstantJs.POSITION_KEY
    layer: string; // BannerConstantJs.IN_LAYER
    cow: string; // BannerConstantJs.PERSONAL_DATA_IS_COMPANYOWNER
    wsz: boolean; // BannerConstantJs.WITH_SITE_ZONE_BANNER_PARAM
    grname: string; // BannerConstantJs.GROUP_NAME_PARAM
    grdesc: string; // BannerConstantJs.GROUP_DESCRIPTION_PARAM
    grpic: string; // BannerConstantJs.GROUP_AVATAR_URL_PARAM
    visible: boolean; // BannerConstantJs.VISIBLE
    act: boolean; // BannerConstantJs.APPS_BANNERS_WITH_CUSTOM_TITLE
    _STRING: string; // BannerConstantJs.APPS_BANNERS_APP_SHORT_NAME
    rfd: number; // BannerConstantJs.EXPR_DELAY
    sz: string; // BannerConstantJs.SITE_ZONE_BANNER_PARAM
    sitename: string; // BannerConstantJs.SITENAME
    passWidth: boolean;
    passHeight: boolean;
}

export class BaseBannerHookJs<D extends IBannerJson> {
    el: HTMLElement;
    hookType: string;
    hookId: string;
    name: string;
    log: (...args: (PropertyKey | object)[]) => void;
    position: number | null = null;
    banner: IBannerJs | null = null;
    visible: boolean = true;
    params: HookBannerParamsJs | null = null;
    showBannerTimeout: number;
    initResult?: Promise<void>;

    constructor(hookType: string, element: HTMLElement) {
        this.el = element;
        this.hookId = element.getAttribute('data-hook-id')!;
        this.hookType = hookType;
        this.name = this.hookId;
        this.log = createLog(`BaseBannerHookJs[${this.name}]`);
        this.showBannerTimeout = 500;
    }

    needCleanElement() {
        return false;
    }

    activate() {
        this.log('activate');

        if (this.showBannerOnActivate()) {
            this.showBanner();
        } else {
            this.init();
        }
    }

    init(): Promise<void> {
        if (!this.initResult) {
            this.initResult = adbWhitelistDetect()
                .then(
                    () => true,
                    () => false
                )
                .then(adbWhitelistDetected => {
                    this.params = this.readBannerParams(adbWhitelistDetected);
                    if (this.needCleanElement()) {
                        // чтобы не палиться перед adblock
                        this.el.removeAttribute('data-hook-id');
                        this.el.removeAttribute('data-module');
                        this.el.removeAttribute('data-params');
                    }
                })
                .then(() => {
                    this.log('params', this.params!);
                });
        }
        return this.initResult;
    }

    showBanner(): Promise<void> {
        return this.init().then(() => this.doShowBanner());
    }

    doShowBanner(): Promise<void> {
        this.clearBannerContent();
        if (this.params !== null) {
            return new Promise((resolve, reject) => {
                setTimeout(() => {
                    try {
                        const el = this.el;
                        const isActive = el !== null;
                        if (isActive) {
                            this.banner = new BannerProxyJs({
                                createDelegate: () => {
                                    return this.createBannerDelegate(this.params!, el);
                                },
                                key: () => 'banner'
                            });
                            this.banner.init(this.visible);
                            this.banner.show();
                            this.log('banner', this.banner);
                            resolve();
                        }
                    } catch (exception) {
                        const ex = exception as Error;
                        this.log('error', ex);
                        logger.errorEx(ex, `BannerHook-${this.name}`, "init_view_");
                        reject(ex);
                    }
                }, this.showBannerTimeout);
            });
        } else {
            return Promise.reject();
        }
    }

    deactivate() {
        if (this.banner !== null) {
            this.banner.destroy();
            this.banner = null;
        }
    }

    showBannerOnActivate() {
        return true;
    }

    logEmptyBanner(): void {
    }

    createBannerDelegate(params: HookBannerParamsJs, container: HTMLElement): IBannerJs {
        const slot = ValueProviderJs.create<number>(params.getPosition());
        return this.buildInlineBanner(container, params, slot, this.name);
    }

    buildInlineBanner(container: HTMLElement, params: HookBannerParamsJs, slot: ValueProviderJs<number>, bannerName: string): SimpleBannerJs {
        const bannerTransport = params.isUseAdfTransport() ? AdfBannerTransportJs.getInstance() : BannerTransportJs.getInstance();
        const model = new BannerModelJs(slot, params, bannerTransport);
        const view = new BannerViewJs({
            container: container,
            slot: slot,
            adfTransport: params.isUseAdfTransport(),
            inline: true,
            onRender: () => {
                DispatcherJs.get().bannerViewStateChangedEvent.trigger();
            },
            onError: () => {
                this.logEmptyBanner();
            }
        });

        const banner = this.createBanner(view, model);
        banner.setInLayer(params.getInLayer());

        const refreshOnNavigate = new ValueProviderJs<boolean>(() => BannerCfgJs.getRefreshPolicyCfg().getTransition(bannerName));

        const refreshTimeout = new ValueProviderJs<number>(() => BannerCfgJs.getRefreshPolicyCfg().getTimeout(bannerName));

        // добавляем триггеры для обновления баннера
        // при навигации
        banner.attachRefreshTrigger(new NavigationTriggerJs(refreshOnNavigate), "navi_hook");
        // по таймауту
        banner.attachRefreshTrigger(new TimeoutTriggerJs(refreshTimeout), "timeout_hook");

        return banner;
    }

    createBanner(view: BannerViewJs, model: BannerModelJs): SimpleBannerJs {
        return new SimpleBannerJs({
            view: view,
            model: model
        });
    }

    getBannerJsonParams(): null|D {
        const params = this.el.getAttribute("data-params");
        if (params === null) {
            return null;
        }
        return utils.parseJson<D>(params);
    }

    readBannerParams(adbWhitelistDetected: boolean): HookBannerParamsJs | null {
        const json = this.getBannerJsonParams() as Record<string, any>;
        log('bannerParams', json);
        if (json === null) {
            return null;
        }
        const withUniqueId = json[BannerConstantJs.WITH_UNIQUE_ID];
        const withPersonalData = json[BannerConstantJs.WITH_PERSONAL_DATA];
        const withLastApps = json[BannerConstantJs.WITH_LAST_APPS];
        const position = json[BannerConstantJs.POSITION_KEY] || this.position;
        const companyOwner = !!json[BannerConstantJs.PERSONAL_DATA_IS_COMPANYOWNER];
        const withSiteZone = json[BannerConstantJs.WITH_SITE_ZONE_BANNER_PARAM];

        const params = new HookBannerParamsJs(withUniqueId, withPersonalData, withLastApps, position, companyOwner, withSiteZone);
        params.setAdblockWhitelist(adbWhitelistDetected);

        if (json.grname) {
            params.setAuxGroupParameters(
                json.grname,
                json.grdesc,
                json.grpic
            );
        }

        this.visible = json.visible;
        this.readAdditionalParams(json as D, params);
        if (json[BannerConstantJs.APPS_BANNERS_WITH_CUSTOM_TITLE]){
            params.setUseAppsCustomTitle(true);
        }
        if (json[BannerConstantJs.APPS_BANNERS_APP_SHORT_NAME]) {
            params.setAppShortName(json[BannerConstantJs.APPS_BANNERS_APP_SHORT_NAME]);
        }

        params.setDelay(json[BannerConstantJs.EXPR_DELAY] || 50);
        params.setInLayer(json[BannerConstantJs.IN_LAYER] || false);
        if (json.passWidth) {
            const element = this.el;
            const width = BaseBannerHookJs.getInnerWidth(element);
            params.setWidth(width);
        }
        if (json.passHeight) {
            params.setWithViewportHeight(true);
        }
        const siteZone = json[BannerConstantJs.SITE_ZONE_BANNER_PARAM];
        if (siteZone) {
            params.setSiteZone(siteZone);
        }
        const sitename = json[BannerConstantJs.SITENAME];
        if (sitename) {
            params.setSitename(sitename);
        }
        params.contentId = json[BannerConstantJs.CONTENT_ID];

        const useAdfTransport = json[BannerConstantJs.ADF_TRANSPORT_PARAM] || false;
        params.setUseAdfTransport(useAdfTransport);

        const callbacksEnabled = BannerCfgJs.getInstance().callbacksEnabled;
        params.setCallbacksEnabled(callbacksEnabled);

        const withDarkThemeParam = json[BannerConstantJs.WITH_DARK_THEME_PARAM] || false;
        params.setWithDarkThemeParam(withDarkThemeParam);

        const withFpidParam = json[BannerConstantJs.WITH_FPID_PARAM] || false;
        params.setWithFpidParam(withFpidParam);

        return params;
    }

    protected readAdditionalParams(json: D, params: HookBannerParamsJs) {

    }

    clearBannerContent() {
        this.el.innerHTML = '';
    }

    static getInnerWidth(el: HTMLElement): number {
        const computedStyle = window.getComputedStyle(el, null);
        const paddingLeft = parseInt(computedStyle.paddingLeft, 10);
        const paddingRight = parseInt(computedStyle.paddingRight, 10);
        return el.clientWidth - paddingLeft - paddingRight;
    }
}

export class HookBannerParamsJs extends BannerParamsJs {
    position: number;
    companyOwner: boolean;
    withSiteZone: boolean;
    groupName: string | null = null;
    groupDescription: string | null = null;
    groupAvatarUrl: string | null = null;
    useAppsCustomTitle = false;
    appShortName: string | null = null;
    delay = 0;
    width = 0;
    siteZone: string | null = null;
    sitename: string | null = null;
    adblockWhitelist = false;
    useAdfTransport = false;
    /**
     * Флаг, что слоты 350 и 4246 инвертированы
     */
    inv = false;
    inLayer = false;
    contentId: string | null = null;

    constructor(withUniqueId: boolean, withPersonalData: boolean, withLastApps: boolean, position: number, companyOwner: boolean, withSiteZone: boolean) {
        super(withUniqueId, withPersonalData, withLastApps);
        this.position = position;
        this.companyOwner = companyOwner;
        this.withSiteZone = withSiteZone;
    }


    setPosition(position: number) {
        this.position = position;
    }

    setDelay(delay: number) {
        this.delay = delay;
    }

    setInv(inv: boolean) {
        this.inv = inv;
    }

    getDelay(): number {
        return this.delay;
    }

    setInLayer(inLayer: boolean) {
        this.inLayer = inLayer;
    }

    getInLayer(): boolean {
        return this.inLayer;
    }

    setAdblockWhitelist(adblockWhitelist: boolean) {
        this.adblockWhitelist = adblockWhitelist;
    }

    getAdblockWhitelist(): boolean {
        return this.adblockWhitelist;
    }

    setUseAdfTransport(useAdfTransport: boolean) {
        this.useAdfTransport = useAdfTransport;
    }

    isUseAdfTransport(): boolean {
        return this.useAdfTransport;
    }

    getPosition(): number {
        return this.position;
    }

    isCompanyOwner(): boolean {
        return this.companyOwner;
    }

    isAdblockWhitelist() {
        return this.adblockWhitelist;
    }

    setAuxGroupParameters(groupName: string, groupDescription: string, groupAvatarUrl: string) {
        this.groupName = groupName;
        this.groupDescription = groupDescription;
        this.groupAvatarUrl = groupAvatarUrl;
    }

    setUseAppsCustomTitle(useAppsCustomTitle: boolean) {
        this.useAppsCustomTitle = useAppsCustomTitle;
    }

    setAppShortName(appShortName: string) {
        this.appShortName = appShortName;
    }

    getWidth(): number {
        return this.width;
    }

    setWidth(width: number) {
        this.width = width;
    }

    setSiteZone(siteZone: string) {
        this.siteZone = siteZone;
    }

    setSitename(sitename: string) {
        this.sitename = sitename;
    }

    asJSON() {
        const params = BannerParamsJs.prototype.asJSON.call(this);
        if (this.isCompanyOwner()) {
            BannerTransportJs.appendParam(params, "l", "1");
        }
        const banDataProvider = BanDataCfgJs.getInstance();
        log('[banDataProvider]', banDataProvider);
        let siteZoneParam = null;
        if (this.siteZone) {
            siteZoneParam = this.siteZone;
        } else if (this.withSiteZone && banDataProvider.sitezone) {
            siteZoneParam = banDataProvider.sitezone;
        }
        if (siteZoneParam !== null) {
            BannerTransportJs.appendParam(params, BannerConstantJs.SITEZONE_PARAM, siteZoneParam);
        }

        if (banDataProvider.groupId) {
            BannerTransportJs.appendParam(params, BannerConstantJs.GROUP_ID_PARAM, banDataProvider.groupId);
        }
        if (banDataProvider.isaMember) {
            BannerTransportJs.appendParam(params, BannerConstantJs.IS_GROUP_ACTIVE_MEMBER_PARAM, banDataProvider.isaMember);
        }
        if (this.groupName) {
            BannerTransportJs.appendParam(params, BannerConstantJs.GROUP_NAME_PARAM, this.groupName);
        }
        if (this.groupDescription) {
            BannerTransportJs.appendParam(params, BannerConstantJs.GROUP_DESCRIPTION_PARAM, this.groupDescription);
        }
        if (this.groupAvatarUrl) {
            BannerTransportJs.appendParam(params, BannerConstantJs.GROUP_AVATAR_URL_PARAM, this.groupAvatarUrl);
        }
        if (this.useAppsCustomTitle) {
            BannerTransportJs.appendParam(params, BannerConstantJs.APPS_BANNERS_WITH_CUSTOM_TITLE, "1");
        }
        if (this.appShortName) {
            BannerTransportJs.appendParam(params, BannerConstantJs.APPS_BANNERS_APP_SHORT_NAME, this.appShortName);
        }

        if (this.getWidth() > 0) {
            BannerTransportJs.appendParam(params, "w", this.getWidth() + '');
        }

        if (this.adblockWhitelist) {
            BannerTransportJs.appendParam(params, 'd', '1');
        } else {
            if (this.inv) {
                BannerTransportJs.appendParam(params, 'inv', '1');
            }
        }

        if (window.OK.isStateRedesign()) {
            BannerTransportJs.appendParam(params, 'web2021', '1');
        }

        if (this.sitename) {
            BannerTransportJs.appendParam(params, 'sitename', this.sitename);
        }

        if (this.contentId) {
            BannerTransportJs.appendParam(params, 'content_id', this.contentId);
        }

        return params;
    }
}

export default BaseBannerHookJs;
